import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Globe, ArrowLeft, Send } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner, EmptyState, LicenceBadge, Stars, SponsoredTag } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CompanyProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/companies/${slug}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const requestQuote = () => {
    if (!user) { navigate("/login"); return; }
    toast.success("To request a quote, post a job describing your project — matching companies can then respond.");
    navigate(`/post-a-job?company=${slug}`);
  };

  if (notFound) return <div className="propora-container py-16"><EmptyState title="Company not found" subtitle="This listing may be pending review or no longer available." /></div>;
  if (!c) return <Spinner />;

  return (
    <div className="propora-container py-10" data-testid="company-profile-page">
      <Link to="/companies" className="inline-flex items-center gap-1.5 text-sm text-[#4A5568] hover:text-[#007F83] mb-6"><ArrowLeft size={16} /> Back to directory</Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl border border-[#E3E8E8] overflow-hidden shadow-soft">
            <div className="h-56 bg-white border-b border-[#E3E8E8] flex items-center justify-center p-6">
              <img src={c.logo || "https://images.pexels.com/photos/8961034/pexels-photo-8961034.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=800"} alt={c.trading_name} className="max-w-full max-h-full object-contain" />
            </div>
            <div className="p-7">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-display font-extrabold text-3xl text-[#0B1733]">{c.trading_name}</h1>
                {c.promoted && <SponsoredTag />}
                {c.is_example && <span className="text-[11px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Example listing</span>}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-[#4A5568]">
                <span className="inline-flex items-center gap-1"><MapPin size={15} /> {c.emirate}</span>
                <Stars value={c.rating} />
                {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#007F83]"><Globe size={15} /> Website</a>}
              </div>
              {c.licence_checked && <div className="mt-4"><LicenceBadge date={c.licence_check_date} /></div>}
              <p className="mt-5 text-[#4A5568] leading-relaxed">{c.description}</p>

              <h3 className="font-display font-bold text-lg text-[#0B1733] mt-7 mb-3">Services</h3>
              <div className="flex flex-wrap gap-2">
                {(c.services?.length ? c.services : c.service_slugs).map((s) => (
                  <span key={s} className="text-sm bg-[#E8F5F1] text-[#0B1733] px-3 py-1.5 rounded-full">{s}</span>
                ))}
              </div>

              <h3 className="font-display font-bold text-lg text-[#0B1733] mt-7 mb-3">Coverage</h3>
              <div className="flex flex-wrap gap-2">
                {c.coverage?.map((a) => <span key={a} className="text-sm border border-[#E3E8E8] text-[#4A5568] px-3 py-1.5 rounded-full">{a}</span>)}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-[#E3E8E8] p-7 mt-6">
            <h3 className="font-display font-bold text-xl text-[#0B1733] mb-4">Reviews</h3>
            {c.reviews?.length ? (
              <div className="space-y-4">
                {c.reviews.map((r, i) => (
                  <div key={i} className="border-b border-[#E3E8E8] pb-4 last:border-0">
                    <div className="flex items-center gap-2"><Stars value={r.rating} /><span className="text-sm font-semibold text-[#0B1733]">{r.author}</span></div>
                    <p className="text-sm text-[#4A5568] mt-1.5">{r.body}</p>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[#4A5568]">No reviews yet. Reviews can only be left by customers after a completed platform job.</p>}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-3xl border border-[#E3E8E8] p-6 shadow-soft sticky top-24">
            <h3 className="font-display font-bold text-lg text-[#0B1733]">Request a quote</h3>
            <p className="text-sm text-[#4A5568] mt-2">Post a job and this company can respond with a quotation. Your contact details stay private until you choose to share them.</p>
            <Button onClick={requestQuote} data-testid="request-quote-btn" className="w-full mt-4 rounded-full bg-[#007F83] hover:bg-[#006B70] text-white h-11">
              Request a quote <Send size={16} className="ml-1.5" />
            </Button>
            <div className="mt-5 p-3 rounded-xl bg-[#E8F5F1] text-xs text-[#0B1733] leading-relaxed">
              <b>What "Licence checked" means:</b> we confirmed the trade licence document is readable and matches the stated legal identity and activities on the check date. It does not prove service quality, insurance or government endorsement.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
